import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/hooks/useI18n';
import { IInstallment } from '@/lib/builder/models/payment-plan.model';
import {
    MANUAL_PAYMENT_METHODS,
    type ManualPaymentMethod,
} from '@/lib/payments/utils/payment-method';

interface MarkInstallmentPaidModalProps {
    installment: IInstallment | null;
    isOpen: boolean;
    isSaving: boolean;
    formatAmount: (value: number) => string;
    formatDate: (iso: string) => string;
    onClose: () => void;
    onConfirm: (params: { installmentId: string; paymentMethod: ManualPaymentMethod }) => void;
}

/**
 * Conferma per registrare a mano l'incasso di una rata.
 *
 * Non è un doppio click di cortesia: l'operazione scrive una riga in `payments`, blocca
 * importo e scadenza della rata, e sulla prima rata fa passare l'itinerario a completato,
 * facendo scadere le proposte alternative.
 */
export const MarkInstallmentPaidModal = ({
    installment,
    isOpen,
    isSaving,
    formatAmount,
    formatDate,
    onClose,
    onConfirm,
}: MarkInstallmentPaidModalProps) => {
    const { t } = useI18n();
    const [method, setMethod] = useState<ManualPaymentMethod>('bank_transfer');

    // Ogni apertura riparte dal metodo più comune, senza ereditare la scelta precedente.
    useEffect(() => {
        if (isOpen) setMethod('bank_transfer');
    }, [isOpen]);

    if (!installment) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="sm"
            title={t('checkout.paymentPlan.markPaidModal.title')}
            footer={
                /* Niente wrapper: il footer del Modal e' gia' un flex allineato a destra
                   con il suo gap. Annidarne un altro dava due spaziature diverse fra i
                   bottoni di questa modale e quelli di tutte le altre. */
                <>
                    <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        disabled={isSaving}
                        onClick={() =>
                            onConfirm({ installmentId: installment.id, paymentMethod: method })
                        }
                    >
                        {t('checkout.paymentPlan.markPaidModal.confirm')}
                    </Button>
                </>
            }
        >
            {/* Il Modal non mette padding sul contenuto: sta a ogni modale allinearsi ai
                px-6 py-4 di header e footer, come fa l'editor del piano. Senza, il
                riepilogo della rata toccava i bordi del pannello. */}
            <div className="flex flex-col gap-4 px-6 py-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1">
                    <span className="font-medium text-gray-900">{installment.label}</span>
                    <div className="flex items-baseline justify-between">
                        <span className="text-sm text-gray-500">
                            {t('checkout.paymentPlan.dueOn', { date: formatDate(installment.due_date) })}
                        </span>
                        <span className="text-lg font-semibold text-gray-900 tabular-nums">
                            {formatAmount(installment.amount)}
                        </span>
                    </div>
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="manual-payment-method">
                        {t('checkout.paymentPlan.markPaidModal.method')}
                    </Label>
                    <Select value={method} onValueChange={(value) => setMethod(value as ManualPaymentMethod)}>
                        <SelectTrigger id="manual-payment-method">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {MANUAL_PAYMENT_METHODS.map((value) => (
                                <SelectItem key={value} value={value}>
                                    {t(`checkout.paymentPlan.markPaidModal.methods.${value}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-900">
                        {t('checkout.paymentPlan.markPaidModal.warning')}
                    </p>
                </div>
            </div>
        </Modal>
    );
};
